/**
 * テーブル / グラフ間の幅可変ハンドル (**画面横断の共有部品**)。
 *
 * 需要想定 daily と 48点データの 2 画面で使う (proto も
 * `js/panel-resizer.js` を両画面で共有している)。 元は
 * `pages/DemandForecastDaily/components/` の page-local だったが、
 * 48点データからも使うため共有へ昇格した (2026-08-27)。
 *
 * 正本は design/ui-feasibility/components/panel-resizer.md (proto の
 * `.panel-resizer` + `js/panel-resizer.js`)。移植で落としやすい点を明示しておく:
 * - **視覚幅 3px と当たり判定を分ける**。3px を直接掴ませると WCAG 2.5.8 (Target Size)
 *   を大きく割り込みドラッグ開始に失敗するので、`::after` を左右 6px はみ出させる
 *   (absolute なので grid のトラック幅は変わらない)。
 * - **`touch-action: none`** が無いとタッチでページスクロールと競合する。
 * - **キーボード ←/→ と `role="separator"`** は proto に実装済み。移植で落とさない。
 * - ドラッグ中はハンドルだけでなく `document.body` にも `cursor` / `user-select` を当てる。
 *
 * ドラッグは pointer capture で受ける (proto は mousedown + document listener だが、
 * capture ならハンドル外へ出ても追従し、後片付けもブラウザに任せられる)。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'

type Props = {
  /** 現在の可変パネル幅 (px)。aria-valuenow とドラッグ開始点に使う */
  width: number
  min: number
  max: number
  /** キーボード ←/→ の 1 押下あたりの調整量 (px) */
  step: number
  /** persist=false はドラッグ中の逐次更新 */
  onWidthChange: (width: number, persist: boolean) => void
  /** ダブルクリック (保存幅を破棄して既定幅へ) */
  onReset: () => void
  /**
   * 可変パネルがハンドルの**右**にある時に `true` (proto `PanelResizer.attach` の
   * `invert`)。 右パネルが可変だと、 ポインタを右へ動かすほど可変パネルは**狭く**
   * なるので、 delta の符号を反転する。 既定 (`false`) は左パネルが可変。
   */
  invert?: boolean
  /**
   * ハンドルを表示する viewport の下限 (px)。 これ未満は 1 カラムに畳まれ、
   * 幅配分の概念が無いので出さない。 proto の `mqMin` と同値を渡す
   * (需要想定 = 1200 / 48点データ = 1100)。
   */
  minViewportWidth?: number
  /**
   * flex / grid の `order`。 **DOM 順どおりに並ぶ画面では渡さない** (proto の
   * `.panel-resizer` も order を持たない)。 モバイルとデスクトップで並び順が
   * 変わる画面 (需要想定: モバイルはチャートが先頭 / デスクトップは右) だけが、
   * 兄弟と揃えた値を渡す。
   */
  order?: number
}

export const PanelResizer = ({
  width,
  min,
  max,
  step,
  onWidthChange,
  onReset,
  invert = false,
  minViewportWidth = 1200,
  order,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  // ドラッグ中のポインタを識別する。マルチタッチで 2 本目がハンドルに触れた時に
  // 起点が上書きされ、2 本目を離した位置が最終幅として保存されるのを防ぐ。
  const dragOrigin = useRef<{
    pointerId: number
    pointerX: number
    width: number
  } | null>(null)

  // ドラッグ中は body 側にも当てる。ハンドルだけだとポインタが外れた瞬間に
  // カーソルが戻り、テキスト選択も走ってしまう。
  useEffect(() => {
    if (!isDragging) return
    const { style } = document.body
    const prevCursor = style.cursor
    const prevUserSelect = style.userSelect
    style.cursor = 'col-resize'
    style.userSelect = 'none'
    return () => {
      style.cursor = prevCursor
      style.userSelect = prevUserSelect
    }
  }, [isDragging])

  // 通知前に必ず可変範囲へ丸める。max は実コンテナ幅から算出した上限なので、
  // ここを通さないと「表示は上限で止まっているのに保存値だけ伸びる」状態になり、
  // 戻す時に上限を下回るまでドラッグしても見た目が動かない (空振りする)。
  const clampToRange = useCallback(
    (next: number): number => Math.min(max, Math.max(min, next)),
    [max, min],
  )

  /** ポインタ移動量から可変パネルの幅を出す (invert = 右パネルが可変)。 */
  const widthFromDelta = useCallback(
    (originWidth: number, deltaX: number): number =>
      clampToRange(originWidth + (invert ? -deltaX : deltaX)),
    [clampToRange, invert],
  )

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return
      // 既にドラッグ中なら 2 本目以降のポインタは無視する
      if (dragOrigin.current !== null) return
      dragOrigin.current = {
        pointerId: event.pointerId,
        pointerX: event.clientX,
        width,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
      setIsDragging(true)
      event.preventDefault()
    },
    [width],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const origin = dragOrigin.current
      if (origin === null || origin.pointerId !== event.pointerId) return
      onWidthChange(
        widthFromDelta(origin.width, event.clientX - origin.pointerX),
        false,
      )
    },
    [onWidthChange, widthFromDelta],
  )

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const origin = dragOrigin.current
      if (origin === null || origin.pointerId !== event.pointerId) return
      dragOrigin.current = null
      setIsDragging(false)
      // 最終値だけを保存する (ドラッグ中の中間値は書かない)
      onWidthChange(
        widthFromDelta(origin.width, event.clientX - origin.pointerX),
        true,
      )
    },
    [onWidthChange, widthFromDelta],
  )

  // タッチ割り込み等でドラッグが中断された時の後始末。pointercancel は
  // 「操作を無かったことにする」イベントなので、掴んだ時点の幅へ戻す。
  // ドラッグ中は保存していない (persist=false) ため、保存値は掴む前のままで、
  // ここも persist=false でよい (= 中断で幅が確定してしまうのを防ぐ)。
  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const origin = dragOrigin.current
      if (origin === null || origin.pointerId !== event.pointerId) return
      dragOrigin.current = null
      setIsDragging(false)
      onWidthChange(origin.width, false)
    },
    [onWidthChange],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // ←/→ は「ハンドルを動かす向き」 に合わせる (= ドラッグと同じ体感)。
      // invert では → がハンドルを右へ = 右の可変パネルは狭くなる。
      if (event.key === 'ArrowLeft') {
        onWidthChange(widthFromDelta(width, -step), true)
        event.preventDefault()
      } else if (event.key === 'ArrowRight') {
        onWidthChange(widthFromDelta(width, step), true)
        event.preventDefault()
      }
    },
    [onWidthChange, step, width, widthFromDelta],
  )

  return (
    <HintTooltip content={t('common:panelResizer.title')}>
      <Box
        flex="0 0 auto"
        // UI 辞書 components/panel-resizer.md `.panel-resizer { flex: 0 0 auto }`。 横並びの
        // Flex の中で 3px のハンドルが縮まないようにする
        display="none"
        width="3px"
        background={isDragging ? 'Text.accentPrimary' : 'Border.lowEmphasis'}
        borderRadius="base"
        _hover={{ background: 'Text.accentPrimary' }}
        userSelect="none"
        cursor="col-resize"
        aria-label={t('common:panelResizer.label')}
        aria-orientation="vertical"
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={width}
        css={{
          position: 'relative',
          transition: 'background 0.15s ease-out',
          // ハンドルだと分かるドット 3 点。gradient 内はトークン文字列が解決されないため
          // raw 値を使う (frontend の token 規約の許容ケース)。
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '2px',
            height: '24px',
            transform: 'translate(-50%, -50%)',
            backgroundImage: `radial-gradient(circle, ${LightModeSemanticColors.Surface.primary.value} 1px, transparent 1.5px)`,
            backgroundSize: '2px 6px',
            backgroundRepeat: 'repeat-y',
            opacity: 0.9,
          },
          // 当たり判定だけ左右へ広げる (レイアウト幅は 3px のまま)
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '0 -6px',
          },
          // 2 カラムの時だけ表示する。1 カラムでは幅配分の概念が無い
          [`@media (min-width: ${minViewportWidth}px)`]: {
            display: 'block',
            alignSelf: 'stretch',
            // `order` は既定で持たない (= DOM 順に従う)。 proto の
            // `.panel-resizer` も order を持たず、 表 → ハンドル → グラフ の
            // DOM 順だけで間に入る。 DOM 順と表示順が食い違う画面 (需要想定 =
            // モバイルでチャートが先頭) だけが呼び出し側で指定する。
            ...(order !== undefined ? { order } : {}),
          },
        }}
        onDoubleClick={onReset}
        onKeyDown={handleKeyDown}
        onPointerCancel={handlePointerCancel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        role="separator"
        tabIndex={0}
        title={t('common:panelResizer.title')}
        touchAction="none"
      />
    </HintTooltip>
  )
}
